import React from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, Stack, Tooltip } from '@mui/material'
import { Description as DescriptionIcon } from '@mui/icons-material'
import WorkerRequiredButton from '../../../common/WorkerRequiredButton'
import { useSmartTranslation } from '../../../../../generic/hooks/useSmartTranslation'
import { JOB_WIDGET_ACTION, resolveJobModeActions } from '../../../common/jobModeActions'
import PreparationLabel from '../../../common/PreparationLabel'

// Story 113 (S120/S121/S122, RN38): the right-hand button changes ACTION AND LABEL, not just its
// disabled state -- in preparation it closes the preparation, in execution it closes the job. So the
// two buttons are rendered from the resolved descriptors instead of from two hardcoded labels, and
// the variant/colour travels with the action: `Go to execution` must NOT look like the disabled
// finish button whose position it takes (S122).
const BUTTON_STYLE = {
  [JOB_WIDGET_ACTION.PAUSE]: { variant: 'outlined', color: 'warning' },
  [JOB_WIDGET_ACTION.CHANGE_STATUS]: { variant: 'outlined', color: 'warning' },
  [JOB_WIDGET_ACTION.FINISH]: { variant: 'contained', color: 'success' },
  [JOB_WIDGET_ACTION.GO_TO_EXECUTION]: { variant: 'contained', color: 'primary' }
}

/**
 * JobHeader Component
 * Displays the job title, description, and the two action buttons of the station widget.
 *
 * @param {Object} props
 * @param {string} props.jobTitle
 * @param {string} props.jobDescription
 * @param {function} props.onPause
 * @param {function} props.onFinish
 * @param {function} [props.onGoToExecution] Story 113 (S123): closes the preparation and opens the
 *        execution period. One step, no confirmation dialog (RN42).
 * @param {function} [props.onChangeStatus] Story 113 (S124): opens the two-action mode modal.
 * @param {boolean} [props.inPreparation=false] Story 113 (S141/RN51): whether to show the preparation
 *        label beside the fabrication order's name. Only preparation is labelled (D-I6): execution
 *        looks exactly as it did before the story.
 * @param {{left: Object, right: Object}} [props.actions] The resolved layout, from the single shared
 *        point (`common/jobModeActions`). When absent it is resolved here from the legacy props
 *        below, which yields today's Pause + Finish -- so a caller that has not been migrated behaves
 *        exactly as before the story.
 * @param {string} props.governedByAcceptanceTooltip S62 (story 86): why the two buttons are disabled, when
 *        the acceptance form governs the job. Empty string when it does not apply -- MUI renders no tooltip
 *        for an empty title, and `finishDisabled` cannot be used to infer the reason because it is composite
 *        (the mark OR pending parts OR not finishable), so blaming the acceptance form for those would lie.
 */
const JobHeader = ({
  jobTitle, jobDescription, onPause, onFinish, onGoToExecution, onChangeStatus,
  finishDisabled, pauseDisabled, governedByAcceptanceTooltip = '', actions, inPreparation = false
}) => {
  const { t_ } = useSmartTranslation()

  const resolved = actions || resolveJobModeActions({
    finishDisabled,
    pauseDisabled,
    gatedTooltip: governedByAcceptanceTooltip
  })

  // Named `handleModeAction` rather than `HANDLER`: `react/jsx-handler-names` requires whatever sits
  // behind an `onClick` to read as a handler, and CRA's dev server enforces it. The legacy compact
  // widget needed the very same rename for its own map.
  const handleModeAction = {
    [JOB_WIDGET_ACTION.PAUSE]: onPause,
    [JOB_WIDGET_ACTION.FINISH]: onFinish,
    [JOB_WIDGET_ACTION.GO_TO_EXECUTION]: onGoToExecution,
    [JOB_WIDGET_ACTION.CHANGE_STATUS]: onChangeStatus
  }

  // S62 — the Tooltip wraps a Box because a disabled MUI button fires no mouse events, so the
  // explanation would never reach the operator on the very buttons it is about. Same wrapping the
  // assembly area already uses (S61).
  const renderButton = (descriptor) => (
    <Tooltip title={descriptor.tooltip} placement='top' arrow>
      <Box>
        <WorkerRequiredButton
          variant={BUTTON_STYLE[descriptor.action].variant}
          color={BUTTON_STYLE[descriptor.action].color}
          sx={{
            textTransform: 'none', height: 50, fontSize: '1.2rem'
          }}
          disabled={descriptor.disabled}
          onClick={handleModeAction[descriptor.action]}
        >
          {t_(descriptor.label)}
        </WorkerRequiredButton>
      </Box>
    </Tooltip>
  )

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
        <DescriptionIcon sx={{ color: 'primary.main', mt: 0.5, fontSize: 32 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant='overline' display='block' color='text.secondary' sx={{ lineHeight: 1 }}>
            {t_('Order Detail')}
          </Typography>
          {/* S141/RN51: the label sits beside the FO name. RN53: with a long name the NAME yields
              space (ellipsis) while the label stays whole -- hence `minWidth: 0` up the chain and
              `flexShrink: 0` on the label itself. */}
          <Stack direction='row' spacing={1} alignItems='center' sx={{ minWidth: 0 }}>
            <Typography
              variant='h5'
              component='div'
              sx={{ fontWeight: 'bold', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {jobTitle}
            </Typography>
            <PreparationLabel visible={inPreparation} />
          </Stack>
          <Typography variant='subtitle1' color='text.primary' sx={{ fontWeight: 'medium' }}>
            {jobDescription}
          </Typography>
        </Box>
      </Box>
      <Stack direction='row' spacing={1}>
        {renderButton(resolved.left)}
        {renderButton(resolved.right)}
      </Stack>
    </Box>
  )
}

JobHeader.propTypes = {
  jobTitle: PropTypes.string,
  jobDescription: PropTypes.string,
  onPause: PropTypes.func,
  onFinish: PropTypes.func,
  onGoToExecution: PropTypes.func,
  onChangeStatus: PropTypes.func,
  finishDisabled: PropTypes.bool,
  pauseDisabled: PropTypes.bool,
  governedByAcceptanceTooltip: PropTypes.string,
  actions: PropTypes.object,
  inPreparation: PropTypes.bool
}

export default JobHeader
